// @ts-nocheck
/**
 * chat-identity.js — Customer identity, anonymous IDs, cookies, and consent.
 *
 * Reads Shopify customer data from the root element's data- attributes
 * (injected by fleur-chat.liquid) and builds an identity object.
 * Anonymous visitors get a localStorage UUID (cookie fallback).
 *
 * Also manages the PDPA/GDPR consent banner.
 *
 * Exposes:
 *   window.AIFlorist.identity.build(root)       → identity object
 *   window.AIFlorist.identity.getAnonymousId()  → string
 *   window.AIFlorist.identity.showConsent(el, privacyUrl, callbacks)
 *   window.AIFlorist.identity.removeConsent()
 *   window.AIFlorist.identity.setAnonymousEmail(email)
 *   window.AIFlorist.identity.setAnonymousProfile({ email, firstName, lastName })
 *   window.AIFlorist.identity.state              → { consentGiven, consentDeclined, customerLoggedIn }
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};

  // ── Storage keys ───────────────────────────────────────────────────
  var CONSENT_KEY = 'aiflorist_consent';
  var ANON_ID_KEY = 'aiflorist_anon_id';
  var MARKETING_EMAIL_KEY = 'aiflorist_marketing_email';
  var MARKETING_FIRST_NAME_KEY = 'aiflorist_marketing_first_name';
  var MARKETING_LAST_NAME_KEY = 'aiflorist_marketing_last_name';

  // ── State (populated by build()) ───────────────────────────────────
  var state = {
    customerLoggedIn: false,
    customerEmail: null,
    customerName: null,
    customerId: null,
    marketingEmail: localStorage.getItem(MARKETING_EMAIL_KEY) || null,
    marketingFirstName: localStorage.getItem(MARKETING_FIRST_NAME_KEY) || null,
    marketingLastName: localStorage.getItem(MARKETING_LAST_NAME_KEY) || null,
    consentGiven: localStorage.getItem(CONSENT_KEY) === 'yes',
    consentDeclined: localStorage.getItem(CONSENT_KEY) === 'no',
  };

  // ── Build identity from root element data attributes ───────────────
  function build(root) {
    state.customerLoggedIn = root.dataset.customerLoggedIn === 'true';
    state.customerEmail = root.dataset.customerEmail || null;
    state.customerName = root.dataset.customerName || null;
    state.customerId = root.dataset.customerId || null;
    state.marketingEmail = localStorage.getItem(MARKETING_EMAIL_KEY) || null;
    state.marketingFirstName = localStorage.getItem(MARKETING_FIRST_NAME_KEY) || null;
    state.marketingLastName = localStorage.getItem(MARKETING_LAST_NAME_KEY) || null;

    if (!state.customerLoggedIn && state.marketingEmail) {
      state.customerEmail = state.marketingEmail;
    }
    if (!state.customerLoggedIn) {
      var fullName = [state.marketingFirstName, state.marketingLastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (fullName) state.customerName = fullName;
    }

    syncEnhancedConversions({
      email: state.customerEmail || state.marketingEmail,
      firstName: state.customerLoggedIn ? state.customerName : state.marketingFirstName,
      lastName: state.customerLoggedIn ? null : state.marketingLastName,
    });

    return buildIdentityObject();
  }

  function buildIdentityObject() {
    if (state.customerLoggedIn && state.customerId) {
      return {
        id: 'shopify:' + state.customerId,
        identityType: 'shopify',
        email: state.customerEmail,
        name: state.customerName,
        shopifyCustomerId: state.customerId,
      };
    }

    var anonId = getAnonymousId();
    var identity = {
      id: 'anon:' + anonId,
      identityType: 'anonymous',
    };
    if (state.marketingEmail) {
      identity.email = state.marketingEmail;
    }
    var fullName = [state.marketingFirstName, state.marketingLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fullName) {
      identity.name = fullName;
    }
    return identity;
  }

  /** Regenerate identity (e.g. after "Forget Me"). */
  function rebuild() {
    return buildIdentityObject();
  }

  // ── Anonymous ID management ────────────────────────────────────────
  function getAnonymousId() {
    var id = localStorage.getItem(ANON_ID_KEY);
    if (id) return id;

    id = getCookie(ANON_ID_KEY);
    if (id) {
      localStorage.setItem(ANON_ID_KEY, id);
      return id;
    }

    id = generateUUID();
    localStorage.setItem(ANON_ID_KEY, id);
    setCookie(ANON_ID_KEY, id, 365);
    return id;
  }

  function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  // ── Consent banner ─────────────────────────────────────────────────
  var bannerSlotEl = null;

  function showConsent(slotEl, privacyUrl, callbacks) {
    bannerSlotEl = slotEl;

    var privacyLink = privacyUrl
      ? ' <a href="' + privacyUrl + '" target="_blank" class="afc-consent-link">Privacy Policy</a>'
      : '';

    slotEl.innerHTML =
      '<div class="afc-consent" id="afc-consent-banner">' +
        '<span class="afc-consent-text">' +
          'Fleur can remember your preferences &mdash; chats kept 90 days, insights 12 months.' + privacyLink +
        '</span>' +
        '<div class="afc-consent-actions">' +
          '<button class="afc-consent-btn afc-consent-btn--decline" id="afc-consent-decline">No thanks</button>' +
          '<button class="afc-consent-btn afc-consent-btn--accept" id="afc-consent-accept">Got it</button>' +
        '</div>' +
      '</div>';

    document.getElementById('afc-consent-accept').addEventListener('click', function () {
      localStorage.setItem(CONSENT_KEY, 'yes');
      state.consentGiven = true;
      removeConsent();
      if (callbacks && callbacks.onAccept) callbacks.onAccept();
    });

    document.getElementById('afc-consent-decline').addEventListener('click', function () {
      localStorage.setItem(CONSENT_KEY, 'no');
      state.consentDeclined = true;
      removeConsent();
      if (callbacks && callbacks.onDecline) callbacks.onDecline();
    });
  }

  function removeConsent() {
    var banner = document.getElementById('afc-consent-banner');
    if (banner) banner.remove();
  }

  /** Clear all identity-related storage (for "Forget Me"). */
  function clearStorage() {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(ANON_ID_KEY);
    localStorage.removeItem(MARKETING_EMAIL_KEY);
    localStorage.removeItem(MARKETING_FIRST_NAME_KEY);
    localStorage.removeItem(MARKETING_LAST_NAME_KEY);
    state.consentGiven = false;
    state.consentDeclined = false;
    state.marketingEmail = null;
    state.marketingFirstName = null;
    state.marketingLastName = null;
  }

  function setAnonymousEmail(email) {
    var normalized = String(email || '').trim().toLowerCase();
    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return false;
    localStorage.setItem(MARKETING_EMAIL_KEY, normalized);
    state.marketingEmail = normalized;
    if (!state.customerLoggedIn) {
      state.customerEmail = normalized;
    }
    syncEnhancedConversions({ email: normalized });
    return true;
  }

  function setAnonymousProfile(profile) {
    if (!profile) return false;
    var hasFirstName = Object.prototype.hasOwnProperty.call(profile, 'firstName');
    var hasLastName = Object.prototype.hasOwnProperty.call(profile, 'lastName');
    var firstName = String(profile.firstName || '').trim();
    var lastName = String(profile.lastName || '').trim();

    if (hasFirstName) {
      if (firstName) {
        localStorage.setItem(MARKETING_FIRST_NAME_KEY, firstName);
        state.marketingFirstName = firstName;
      } else {
        localStorage.removeItem(MARKETING_FIRST_NAME_KEY);
        state.marketingFirstName = null;
      }
    }

    if (hasLastName) {
      if (lastName) {
        localStorage.setItem(MARKETING_LAST_NAME_KEY, lastName);
        state.marketingLastName = lastName;
      } else {
        localStorage.removeItem(MARKETING_LAST_NAME_KEY);
        state.marketingLastName = null;
      }
    }

    if (!state.customerLoggedIn) {
      var fullName = [state.marketingFirstName, state.marketingLastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      state.customerName = fullName || null;
    }
    if (profile.email) {
      setAnonymousEmail(profile.email);
    }
    syncEnhancedConversions({
      email: state.customerEmail || state.marketingEmail,
      firstName: state.marketingFirstName,
      lastName: state.marketingLastName,
    });
    return true;
  }

  function syncEnhancedConversions(profile) {
    var enhancedConversions = window.AIFlorist && window.AIFlorist.enhancedConversions;
    if (!enhancedConversions || typeof enhancedConversions.rememberBuyerProfile !== 'function') return;
    enhancedConversions.rememberBuyerProfile(profile);
  }

  // ── Public API ─────────────────────────────────────────────────────
  window.AIFlorist.identity = {
    build: build,
    rebuild: rebuild,
    getAnonymousId: getAnonymousId,
    showConsent: showConsent,
    removeConsent: removeConsent,
    setAnonymousEmail: setAnonymousEmail,
    setAnonymousProfile: setAnonymousProfile,
    clearStorage: clearStorage,
    state: state,
    CONSENT_KEY: CONSENT_KEY,
    ANON_ID_KEY: ANON_ID_KEY,
    MARKETING_EMAIL_KEY: MARKETING_EMAIL_KEY,
    MARKETING_FIRST_NAME_KEY: MARKETING_FIRST_NAME_KEY,
    MARKETING_LAST_NAME_KEY: MARKETING_LAST_NAME_KEY,
  };
})();
